import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { Z_INDEX } from '../../utils/zIndex';
import Icon from './Icon';
import CloseButton from './CloseButton';
import { colors } from '../../styles/colors';

/**
 * Milestone definitions - non-translatable data (icon, confetti flag) keyed
 * by milestone id. Title/message text lives in
 * src/locales/en/onboarding.json under milestoneCelebration.<key>.
 *
 * Only the real moments celebrate (Kurtis, 2026-10-01): your first trip and
 * the first time you share one. The small toasts — first search, first save,
 * "Discover unlocked" after a tour, the "Travel Pro" checklist finale — were
 * cut with the other onboarding interruptions; a badge unlock still gets its
 * own modal (BadgeUnlockCelebration).
 */
const MILESTONE_CELEBRATIONS = {
  firstTripCreated: {
    icon: 'airplane',
    confetti: true
  },
  firstTripShared: {
    icon: 'share',
    confetti: true
  }
};

/**
 * Simple confetti burst animation
 * (exported for reuse — BadgeUnlockCelebration fires the same burst)
 */
export function ConfettiBurst() {
  const canvasRef = useRef(null);

  useEffect(() => {
    // Canvas animation runs outside CSS, so the global prefers-reduced-motion rule can't reach it
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    // Design system aligned celebration colors
    const confettiColors = [
      colors.error.DEFAULT,
      colors.success.DEFAULT,
      colors.primary.DEFAULT,
      colors.warning.DEFAULT,
      colors.accent.DEFAULT,
      colors.info.DEFAULT,
      colors.primary.light
    ];

    // Create particles
    for (let i = 0; i < 100; i++) {
      particles.push({
        x: canvas.width / 2,
        y: canvas.height / 2,
        vx: (Math.random() - 0.5) * 15,
        vy: (Math.random() - 0.5) * 15 - 5,
        color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
        size: Math.random() * 8 + 4,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        gravity: 0.3,
        opacity: 1
      });
    }

    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let allGone = true;
      particles.forEach(p => {
        if (p.opacity <= 0) return;
        allGone = false;

        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        p.opacity -= 0.015;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        ctx.restore();
      });

      if (!allGone) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animate();

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: Z_INDEX.TOAST + 1 }}
    />
  );
}

/**
 * Celebration Toast Component
 */
function CelebrationToast({ celebration, onClose }) {
  const { t } = useTranslation('onboarding');
  const [isVisible, setIsVisible] = useState(false);

  const close = useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for animation
  }, [onClose]);

  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => setIsVisible(true));

    // Auto-close after 4 seconds
    const timer = setTimeout(close, 4000);
    return () => clearTimeout(timer);
  }, [close]);

  return createPortal(
    <>
      {celebration.confetti && <ConfettiBurst />}
      {/* Full-width row with a centred card, not a left:50% box: a fixed box
          with no width sizes itself into the half of the screen right of its
          left edge, which on a phone wrapped the title one word per line.
          Phones have no top bar, so it sits just under the safe area there
          and below the 64px nav from md up. */}
      <div
        className={`fixed inset-x-0 top-[calc(env(safe-area-inset-top)+1rem)] md:top-20 px-4 flex justify-center pointer-events-none transition-all duration-normal motion-reduce:transition-none ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
        }`}
        style={{ zIndex: Z_INDEX.TOAST }}
      >
        <div
          role="status"
          className="pointer-events-auto w-full max-w-sm bg-surface rounded-card border border-border shadow-elevation-4 p-4 flex items-start gap-3"
        >
          {/* Coral: a celebration is the warm moment it's for */}
          <span className="flex-shrink-0 w-10 h-10 rounded-full bg-coral text-coral-ink flex items-center justify-center" aria-hidden="true">
            <Icon name={celebration.icon} className="w-5 h-5" />
          </span>

          <div className="flex-1 min-w-0 pt-0.5">
            <h3 className="font-display font-medium text-xl leading-tight text-content-primary">
              {t(`milestoneCelebration.${celebration.key}.title`)}
            </h3>
            <p className="text-sm text-content-secondary mt-0.5">
              {t(`milestoneCelebration.${celebration.key}.message`)}
            </p>
          </div>

          <CloseButton
            variant="plain"
            onClick={close}
            ariaLabel={t('milestoneCelebration.dismissAriaLabel')}
            className="-mt-2 -mr-2"
          />
        </div>
      </div>
    </>,
    document.body
  );
}

/**
 * MilestoneCelebration Provider Component
 *
 * Celebrates a milestone the moment it flips from false to true while the
 * app is open. Milestones live on the server and only ever flip once, so that
 * is the whole "already shown" rule: nothing is stored on the device. The
 * baseline is taken when a user's onboarding state finishes loading, so the
 * milestones the server already has never count as new — they did, through a
 * localStorage list that was empty on every fresh device and browser, and
 * the last of them popped on load. That list was also shared by every account
 * on the device, so a second account missed celebrations the first had seen.
 */
export function MilestoneCelebrationProvider({ children }) {
  const { milestones, isLoading } = useOnboarding();
  const [celebration, setCelebration] = useState(null);
  // null = no baseline yet (still loading, or a different user is loading)
  const baselineRef = useRef(null);

  useEffect(() => {
    if (isLoading) {
      baselineRef.current = null;
      return;
    }
    if (baselineRef.current === null) {
      baselineRef.current = { ...milestones };
      return;
    }

    const previous = baselineRef.current;
    const newlyDone = Object.keys(MILESTONE_CELEBRATIONS)
      .find(key => milestones[key] && !previous[key]);
    if (newlyDone) {
      setCelebration({ key: newlyDone, ...MILESTONE_CELEBRATIONS[newlyDone] });
    }
    baselineRef.current = { ...milestones };
  }, [milestones, isLoading]);

  const handleClose = useCallback(() => {
    setCelebration(null);
  }, []);

  return (
    <>
      {children}
      {/* Keyed so a second celebration remounts with its own 4s timer
          instead of inheriting the first one's */}
      {celebration && (
        <CelebrationToast key={celebration.key} celebration={celebration} onClose={handleClose} />
      )}
    </>
  );
}

export default MilestoneCelebrationProvider;
